import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { bookingAPI, notificationAPI, driverChatAPI } from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { showError, showLoading, updateToSuccess, updateToError, showSuccess } from '../utils/toast';
import { handlePhoneCall } from '../utils/phoneHandler';

const DriverNotification = () => {
  const { notificationId } = useParams();
  const navigate = useNavigate();
  const [notification, setNotification] = useState(null);
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeclineForm, setShowDeclineForm] = useState(false);
  const [declineReason, setDeclineReason] = useState('');
  const [showChat, setShowChat] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [userMessages, setUserMessages] = useState([]);
  const [showUserMessages, setShowUserMessages] = useState(false);

  useEffect(() => {
    fetchNotificationDetails();
  }, [notificationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchNotificationDetails = async () => {
    try {
      const driverEmail = localStorage.getItem('driverEmail');
      if (!driverEmail) {
        setError('Please login as driver first');
        showError('❌ Please login as driver first');
        return;
      }

      // Get all notifications for the driver
      const notificationsResponse = await notificationAPI.getNotifications(driverEmail);
      const foundNotification = notificationsResponse.data.find(n => n.id === parseInt(notificationId));
      
      if (!foundNotification) {
        setError('Notification not found');
        showError('❌ Notification not found');
        return;
      }
      
      setNotification(foundNotification);

      // If notification has a booking, fetch booking details
      if (foundNotification.booking) {
        try {
          const bookingId = foundNotification.booking.toString();
          let bookingData = null;
          
          // First try direct fetch by ID if we have a numeric ID
          try {
            const directResponse = await bookingAPI.getBooking(bookingId);
            if (directResponse.data) {
              bookingData = directResponse.data;
            }
          } catch (directError) {
            console.log('Direct fetch failed, trying search...', directError);
            // If direct fetch fails, try search with padded ID
            const paddedId = bookingId.padStart(3, '0');
            const searchResponse = await bookingAPI.search(paddedId, 'booking_id');
            if (searchResponse.data && searchResponse.data.length > 0) {
              bookingData = searchResponse.data[0];
            }
          }
          
          if (bookingData) {
            setBooking(bookingData);
          } else {
            console.warn('No booking data found for ID:', bookingId);
          }
        } catch (bookingError) {
          console.error('Error fetching booking details:', bookingError);
          showError('⚠️ Could not load booking details');
        }
      }

      // Mark notification as read
      if (!foundNotification.is_read) {
        try {
          await notificationAPI.markAsRead(notificationId);
        } catch (markReadError) {
          console.error('Error marking notification as read:', markReadError);
        }
      }

    } catch (err) {
      console.error('Error fetching notification details:', err);
      setError('Failed to load notification details');
      showError('❌ Failed to load notification details');
    } finally {
      setLoading(false);
    }
  };

  const extractBookingIdFromMessage = (message) => {
    const match = message.match(/Booking ID: #(\d+)/);
    return match ? parseInt(match[1]) : null;
  };

  // Chat functions
  const fetchChatHistory = async () => {
    if (!booking?.id) return;
    
    try {
      setChatLoading(true);
      const response = await driverChatAPI.getChatHistory(booking.id);
      setChatMessages(response.data || []);
    } catch (error) {
      console.error('Error fetching chat history:', error);
      showError('Failed to load chat history');
    } finally {
      setChatLoading(false);
    }
  };

  const sendChatMessage = async () => {
    if (!newMessage.trim() || !booking?.id) return;

    const messageText = newMessage.trim();
    setNewMessage('');

    try {
      const chatData = {
        booking_id: booking.id,
        message: messageText,
        sender_type: 'driver'
      };

      await driverChatAPI.sendMessage(chatData);
      
      // Add message to local state immediately
      const newMsg = {
        id: Date.now(),
        message: messageText,
        sender_type: 'driver',
        created_at: new Date().toISOString(),
        sender_name: 'You'
      };
      
      setChatMessages(prev => [...prev, newMsg]);
      showSuccess('Message sent to user');
      
      // Refresh chat history
      setTimeout(() => {
        fetchChatHistory();
      }, 1000);
      
    } catch (error) {
      console.error('Error sending message:', error);
      showError('Failed to send message');
      setNewMessage(messageText); // Restore message on error
    }
  };

  const toggleChat = () => {
    setShowChat(!showChat);
    if (!showChat && booking?.id) {
      fetchChatHistory();
    }
  };

  const fetchUserMessages = async () => {
    try {
      const response = await driverChatAPI.getUserMessages();
      setUserMessages(response.data || []);
    } catch (error) {
      console.error('Error fetching user messages:', error);
      // If endpoint doesn't exist (404), set empty array and show info message
      if (error.response?.status === 404) {
        console.warn('User messages endpoint not implemented yet');
        setUserMessages([]);
        showError('⚠️ User messages feature not yet available on backend');
      } else {
        setUserMessages([]);
        showError('Failed to load user messages');
      }
    }
  };

  const toggleUserMessages = () => {
    setShowUserMessages(!showUserMessages);
    if (!showUserMessages) {
      fetchUserMessages();
    }
  };

  const handleDriverResponse = async (responseType, message = '') => {
    const loadingToast = showLoading(`Sending ${responseType} response...`);
    
    try {
      const driverEmail = localStorage.getItem('driverEmail');
      
      // Extract booking ID from notification message or use booking.id
      const bookingId = booking?.id || extractBookingIdFromMessage(notification?.message || '');
      
      if (!bookingId) {
        updateToError(loadingToast, '❌ Could not determine booking ID. Please contact admin.');
        return;
      }
      
      await notificationAPI.driverResponse({
        booking_id: bookingId,
        driver_email: driverEmail,
        response_type: responseType,
        message: message
      });
      
      const responseMessages = {
        accept: '🎉 Ride accepted! Admin has been notified.',
        decline: '❌ Ride declined. Admin has been notified.',
        arrived: '📍 Arrival confirmed! Customer has been notified.',
        completed: '🏁 Trip completed successfully!'
      };
      
      updateToSuccess(loadingToast, responseMessages[responseType] || '🎉 Response sent successfully!');
      
      if (responseType === 'decline') {
        setShowDeclineForm(false);
        setDeclineReason('');
      }
      
    } catch (error) {
      console.error('Error sending response:', error);
      updateToError(loadingToast, '❌ Failed to send response. Please try again.');
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 'text-gray-600 bg-gray-100';
      case 'confirmed': return 'text-blue-600 bg-blue-100';
      case 'en_route': case 'pickup': return 'text-yellow-600 bg-yellow-100';
      case 'in_progress': return 'text-green-600 bg-green-100';
      case 'completed': return 'text-green-700 bg-green-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-yellow-50 to-yellow-100 pt-20 flex items-center justify-center">
        <LoadingSpinner size="large" text="Loading notification..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-yellow-50 to-yellow-100 pt-20">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-red-50 border border-red-200 rounded-2xl p-8 text-center">
            <div className="text-6xl mb-4">❌</div>
            <h1 className="text-2xl font-bold text-red-800 mb-4">Error</h1>
            <p className="text-red-600 mb-6">{error}</p>
            <button
              onClick={() => navigate('/')}
              className="px-6 py-3 bg-yellow-400 text-black rounded-lg hover:bg-yellow-500 transition-all"
            >
              Go Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-yellow-50 to-yellow-100 pt-20">
      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="mb-4 px-4 py-2 bg-white border border-yellow-200 rounded-lg hover:bg-yellow-50 transition-all"
          >
            ← Back
          </button>
          <h1 className="text-4xl font-bold text-center">
            <span className="bg-gradient-to-r from-gray-800 to-yellow-600 bg-clip-text text-transparent">
              🚗 Driver Notification
            </span>
          </h1>
        </div>

        {/* Notification Details */}
        <div className="bg-white/95 backdrop-blur-md rounded-2xl border border-yellow-200 p-8 mb-8 shadow-lg">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800 mb-2">{notification.title}</h2>
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <span>📅 {new Date(notification.created_at).toLocaleString()}</span>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  notification.notification_type === 'ride_assignment' ? 'bg-blue-100 text-blue-800' :
                  notification.notification_type === 'booking_update' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {notification.notification_type.replace('_', ' ').toUpperCase()}
                </span>
              </div>
            </div>
            <div className={`px-3 py-1 rounded-full text-sm ${
              notification.is_read ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'
            }`}>
              {notification.is_read ? 'Read' : 'New'}
            </div>
          </div>
          
          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-gray-700 whitespace-pre-line">{notification.message}</p>
          </div>
        </div>

        {/* Booking Details */}
        {booking ? (
          <div className="bg-white/95 backdrop-blur-md rounded-2xl border border-yellow-200 p-8 shadow-lg">
            <h3 className="text-2xl font-bold text-gray-800 mb-6">📋 Booking Details</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Customer Information */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-800 border-b border-yellow-200 pb-2">
                  👤 Customer Information
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Name:</span>
                    <span className="font-semibold text-gray-800">{booking.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Phone:</span>
                    <span className="font-semibold text-gray-800">
                      <button
                        type="button"
                        onClick={(e) => handlePhoneCall(booking.phone, e)}
                        className="text-blue-600 hover:underline cursor-pointer bg-transparent border-none p-0 font-semibold"
                        data-phone="true"
                      >
                        {booking.phone}
                      </button>
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Email:</span>
                    <span className="font-semibold text-gray-800">{booking.email}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Passengers:</span>
                    <span className="font-semibold text-gray-800">{booking.passengers}</span>
                  </div>
                </div>
              </div>

              {/* Trip Information */}
              <div className="space-y-4">
                <h4 className="text-lg font-semibold text-gray-800 border-b border-yellow-200 pb-2">
                  🚗 Trip Information
                </h4>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Booking ID:</span>
                    <span className="font-semibold text-gray-800">#{booking.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Service:</span>
                    <span className="font-semibold text-gray-800 capitalize">{booking.service_type}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Date:</span>
                    <span className="font-semibold text-gray-800">{booking.pickup_date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Time:</span>
                    <span className="font-semibold text-gray-800">{booking.pickup_time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className={`px-2 py-1 rounded-full text-xs font-semibold ${getStatusColor(booking.status)}`}>
                      {booking.status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Route Information */}
            <div className="mt-8">
              <h4 className="text-lg font-semibold text-gray-800 border-b border-yellow-200 pb-2 mb-4">
                📍 Route Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <span className="text-green-600 mr-2">🟢</span>
                    <span className="font-semibold text-gray-800">Pickup Location</span>
                  </div>
                  <p className="text-gray-700">{booking.pickup_address}</p>
                </div>
                <div className="bg-red-50 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <span className="text-red-600 mr-2">🔴</span>
                    <span className="font-semibold text-gray-800">Destination</span>
                  </div>
                  <p className="text-gray-700">{booking.dropoff_address}</p>
                </div>
              </div>
            </div>

            {/* Additional Information */}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-lg font-semibold text-gray-800 border-b border-yellow-200 pb-2 mb-4">
                  💳 Payment Information
                </h4>
                <div className="bg-blue-50 rounded-lg p-4">
                  <span className="font-semibold text-gray-800 capitalize">{booking.payment_method}</span>
                </div>
              </div>
              
              {booking.special_requests && (
                <div>
                  <h4 className="text-lg font-semibold text-gray-800 border-b border-yellow-200 pb-2 mb-4">
                    📝 Special Requests
                  </h4>
                  <div className="bg-yellow-50 rounded-lg p-4">
                    <p className="text-gray-700">{booking.special_requests}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Contact & Navigation Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <button
                type="button"
                onClick={(e) => handlePhoneCall(booking.phone, e)}
                className="flex-1 px-6 py-3 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-all text-center cursor-pointer"
                data-phone="true"
              >
                📞 Call Customer
              </button>
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(booking.pickup_address)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-all text-center"
              >
                🗺️ Navigate to Pickup
              </a>
            </div>
          </div>
        ) : null}
        
        {/* DRIVER ACTION BUTTONS - ALWAYS SHOW IF NOTIFICATION EXISTS */}
        {notification && (
          <div style={{marginTop: '32px', padding: '24px', backgroundColor: '#ffffff', border: '3px solid #000000', borderRadius: '12px'}}>
            <h3 style={{fontSize: '24px', fontWeight: 'bold', color: '#000000', marginBottom: '20px', textAlign: 'center'}}>
              🚗 DRIVER RESPONSE REQUIRED
            </h3>
            
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px'}}>
              <button
                onClick={() => handleDriverResponse('accept')}
                style={{
                  padding: '20px',
                  backgroundColor: '#22c55e',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
              >
                ✅ ACCEPT Ride
              </button>
              
              <button
                onClick={() => setShowDeclineForm(true)}
                style={{
                  padding: '20px',
                  backgroundColor: '#ef4444',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
              >
                ❌ DECLINE Ride
              </button>
            </div>
            
            {/* Chat Button */}
            <div style={{marginTop: '20px'}}>
              <button
                onClick={toggleChat}
                style={{
                  width: '100%',
                  padding: '15px',
                  backgroundColor: '#f59e0b',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
              >
                💬 {showChat ? 'Hide Chat' : 'Chat with User'}
              </button>
            </div>
            
            {/* User Messages Button */}
            <div style={{marginTop: '10px'}}>
              <button
                onClick={toggleUserMessages}
                style={{
                  width: '100%',
                  padding: '15px',
                  backgroundColor: '#8b5cf6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
              >
                📨 {showUserMessages ? 'Hide User Messages' : 'View User Messages'}
              </button>
            </div>
            
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px'}}>
              <button
                onClick={() => handleDriverResponse('arrived')}
                style={{
                  padding: '20px',
                  backgroundColor: '#eab308',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
              >
                📍 ARRIVED AT PICKUP
              </button>
              
              <button
                onClick={() => handleDriverResponse('completed')}
                style={{
                  padding: '20px',
                  backgroundColor: '#8b5cf6',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '18px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
              >
                🏁 Ride COMPLETED
              </button>
            </div>
            
            {showDeclineForm && (
              <div style={{marginTop: '20px', padding: '20px', backgroundColor: '#fef2f2', border: '2px solid #fecaca', borderRadius: '8px'}}>
                <h4 style={{color: '#dc2626', marginBottom: '12px', fontSize: '18px'}}>Reason for Declining:</h4>
                <textarea
                  value={declineReason}
                  onChange={(e) => setDeclineReason(e.target.value)}
                  placeholder="Please provide a reason for declining this assignment..."
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    marginBottom: '12px',
                    minHeight: '100px',
                    fontSize: '16px'
                  }}
                />
                <div style={{display: 'flex', gap: '12px'}}>
                  <button
                    onClick={() => handleDriverResponse('decline', declineReason)}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#dc2626',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}
                  >
                    Submit Decline
                  </button>
                  <button
                    onClick={() => {
                      setShowDeclineForm(false);
                      setDeclineReason('');
                    }}
                    style={{
                      padding: '12px 24px',
                      backgroundColor: '#6b7280',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontSize: '16px'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
            
            {/* User Messages Interface */}
            {showUserMessages && (
              <div style={{
                marginTop: '20px',
                padding: '20px',
                backgroundColor: '#f3f4f6',
                border: '2px solid #d1d5db',
                borderRadius: '12px'
              }}>
                <h4 style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#374151',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  📨 Messages from Users
                </h4>
                
                {userMessages.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: '#6b7280'
                  }}>
                    <div style={{fontSize: '24px', marginBottom: '8px'}}>📨</div>
                    <div>No user messages yet</div>
                    <div style={{fontSize: '14px'}}>Messages from users will appear here</div>
                  </div>
                ) : (
                  <div style={{
                    maxHeight: '300px',
                    overflowY: 'auto',
                    backgroundColor: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    padding: '12px'
                  }}>
                    {userMessages.map((msg, index) => (
                      <div key={msg.id || index} style={{
                        marginBottom: '16px',
                        padding: '12px',
                        backgroundColor: '#fef3c7',
                        border: '1px solid #fbbf24',
                        borderRadius: '8px'
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginBottom: '8px'
                        }}>
                          <div style={{
                            fontSize: '14px',
                            fontWeight: 'bold',
                            color: '#92400e'
                          }}>
                            👤 {msg.sender_name || 'User'}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#6b7280'
                          }}>
                            {new Date(msg.created_at).toLocaleString()}
                          </div>
                        </div>
                        <div style={{
                          color: '#374151',
                          fontSize: '14px',
                          lineHeight: '1.5'
                        }}>
                          {msg.message}
                        </div>
                        {msg.sender_email && (
                          <div style={{
                            fontSize: '12px',
                            color: '#6b7280',
                            marginTop: '4px'
                          }}>
                            📧 {msg.sender_email}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {/* Chat Interface */}
            {showChat && booking && (
              <div style={{
                marginTop: '20px',
                padding: '20px',
                backgroundColor: '#f9fafb',
                border: '2px solid #e5e7eb',
                borderRadius: '12px'
              }}>
                <h4 style={{
                  fontSize: '18px',
                  fontWeight: 'bold',
                  color: '#374151',
                  marginBottom: '16px',
                  textAlign: 'center'
                }}>
                  💬 Chat with User - Booking #{booking.id}
                </h4>
                
                {/* Chat Messages */}
                <div style={{
                  height: '300px',
                  overflowY: 'auto',
                  backgroundColor: '#ffffff',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  padding: '12px',
                  marginBottom: '16px'
                }}>
                  {chatLoading ? (
                    <div style={{textAlign: 'center', padding: '20px'}}>
                      <div>Loading chat...</div>
                    </div>
                  ) : chatMessages.length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '40px',
                      color: '#6b7280'
                    }}>
                      <div style={{fontSize: '24px', marginBottom: '8px'}}>💬</div>
                      <div>No messages yet</div>
                      <div style={{fontSize: '14px'}}>Start a conversation with the user</div>
                    </div>
                  ) : (
                    chatMessages.map((msg, index) => {
                      const isDriver = msg.sender_type === 'driver';
                      return (
                        <div key={msg.id || index} style={{
                          marginBottom: '12px',
                          display: 'flex',
                          justifyContent: isDriver ? 'flex-end' : 'flex-start'
                        }}>
                          <div style={{
                            maxWidth: '70%',
                            padding: '8px 12px',
                            borderRadius: '12px',
                            backgroundColor: isDriver ? '#f59e0b' : '#e5e7eb',
                            color: isDriver ? '#ffffff' : '#374151'
                          }}>
                            <div style={{
                              fontSize: '12px',
                              opacity: 0.8,
                              marginBottom: '4px'
                            }}>
                              {isDriver ? '🚗 You' : '👤 User'}
                            </div>
                            <div>{msg.message}</div>
                            <div style={{
                              fontSize: '10px',
                              opacity: 0.7,
                              marginTop: '4px'
                            }}>
                              {new Date(msg.created_at).toLocaleTimeString()}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                
                {/* Message Input */}
                <div style={{display: 'flex', gap: '8px'}}>
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message to the user..."
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      resize: 'none',
                      fontSize: '16px',
                      minHeight: '60px'
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendChatMessage();
                      }
                    }}
                  />
                  <button
                    onClick={sendChatMessage}
                    disabled={!newMessage.trim()}
                    style={{
                      padding: '12px 16px',
                      backgroundColor: newMessage.trim() ? '#10b981' : '#9ca3af',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: newMessage.trim() ? 'pointer' : 'not-allowed',
                      fontSize: '16px',
                      fontWeight: 'bold'
                    }}
                  >
                    📤 Send
                  </button>
                </div>
                
                <div style={{
                  fontSize: '12px',
                  color: '#6b7280',
                  textAlign: 'center',
                  marginTop: '8px'
                }}>
                  Press Enter to send, Shift+Enter for new line
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DriverNotification;